import { useState, useEffect } from 'react';
import { myChannelService } from '../service/myChannelService';
import { useGlobalPostState } from '../contexts/global-post-state-context';
import { useTranslation } from './useTranslation';
import { useSnackbar } from '../contexts/snack-bar-provider';
import { logger } from '../lib/logger';

interface UseFollowUserProps {
    userId: string;
    initialIsFollowing?: boolean;
    onSuccess?: () => void;
}

export const useFollowUser = ({ userId, initialIsFollowing = false, onSuccess }: UseFollowUserProps) => {
    const { t } = useTranslation();
    const { addSnackbar } = useSnackbar();
    const globalPostState = useGlobalPostState();

    const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
    const [isLoading, setIsLoading] = useState(false);
    
    // For Modals
    const [bonusPoints, setBonusPoints] = useState<number>(0);
    const [showBonusModal, setShowBonusModal] = useState<boolean>(false);
    const [levelUpData, setLevelUpData] = useState<any>(null);
    const [showLevelUpModal, setShowLevelUpModal] = useState<boolean>(false);

    // Sync with global state
    useEffect(() => {
        const unsubscribe = globalPostState.subscribe((update) => {
            if (update.type === 'FOLLOW_STATUS_CHANGED') {
                const payload = update.payload as any;
                if (String(payload.userId) === String(userId)) {
                    setIsFollowing(payload.isFollowing);
                }
            }
        });
        return unsubscribe;
    }, [globalPostState, userId]);

    // Handle initial prop change
    useEffect(() => {
        setIsFollowing(initialIsFollowing);
    }, [initialIsFollowing]);

    const handleFollowToggle = async () => {
        if (!userId || isLoading) return;
        
        const isTogglingToFollow = !isFollowing;
        setIsLoading(true);

        try {
            let response;
            if (isTogglingToFollow) {
                response = await myChannelService.followUser(userId);
                
                // Handle Bonus Points & Level Up
                if (response?.data?.bonus_point && response.data.bonus_point > 0) {
                    setBonusPoints(response.data.bonus_point);
                    setShowBonusModal(true);
                    if (response?.data?.level_up?.level_new) {
                        setLevelUpData(response.data.level_up);
                    }
                } else if (response?.data?.level_up?.level_new) {
                    setLevelUpData(response.data.level_up);
                    setShowLevelUpModal(true);
                }
            } else {
                await myChannelService.unfollowUser(userId);
            }

            // Sync globally
            globalPostState.updateUserFollowStatus(userId, isTogglingToFollow, 'useFollowUser');
            
            // Notification
            addSnackbar({ 
                message: isTogglingToFollow ? t('follow_success', '팔로우 되었습니다.') : t('unfollow_success', '언팔로우 되었습니다.'), 
                severity: 'success',
                type: 'toast'
            });

            onSuccess?.();
        } catch (error) {
            logger.error('Follow toggle error:', error);
            addSnackbar({ 
                message: t('action_failed', '처리 중 오류가 발생했습니다.'), 
                severity: 'error',
                type: 'toast'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isFollowing,
        isLoading,
        handleFollowToggle,
        bonusPoints,
        showBonusModal,
        setShowBonusModal,
        levelUpData,
        showLevelUpModal,
        setShowLevelUpModal
    };
};
